import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';

import { BaseModal, Button, Loader } from 'src/components';
import {
  changePlan,
  previewPlanChange,
  TPlanChangePreview,
} from 'src/api/functions/subscriptionApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import useModal from 'src/hooks/useModal';
import messages from './messages';
import styles from './styles.module.scss';

/**
 * Change-plan confirmation modal (Phase 4).
 *
 * UPGRADE   → applies immediately, once the prorated difference is charged
 *             (the preview says how much; a declined charge changes nothing).
 * DOWNGRADE → scheduled for the next renewal; nothing charged now.
 *
 * Fetches a preview to set expectations, then applies on confirm and toasts the
 * outcome. Rendered via `useModal().showModal(<ChangePlanModal targetCode=... />)`.
 */
const ChangePlanModal: React.FC<{ targetCode: string }> = ({ targetCode }) => {
  const intl = useIntl();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlerts();
  const { closeModal } = useModal();

  const [preview, setPreview] = useState<TPlanChangePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  // A 400 carries the server's own reason (e.g. "A payment method is required
  // to make this change…"); say that rather than "please try again", which a
  // retry can never fix.
  const [serverMessage, setServerMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await previewPlanChange(targetCode, accessToken);
      if (cancelled) return;
      if (isErrorResponseResult(result)) {
        setError(true);
      } else {
        setPreview(result as unknown as TPlanChangePreview);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [targetCode, accessToken]);

  const planName = preview?.target_plan_name ?? targetCode;
  const isUpgrade = !!preview?.is_upgrade;
  const amountNow =
    isUpgrade && preview?.amount_due_now_usd != null
      ? intl.formatNumber(preview.amount_due_now_usd, {
          style: 'currency',
          currency: (preview.currency || 'usd').toUpperCase(),
        })
      : null;

  const onConfirm = async () => {
    setSubmitting(true);
    setError(false);
    setPaymentFailed(false);
    setServerMessage(null);
    try {
      const result = await changePlan(targetCode, accessToken, preview?.proration_date);
      if (isErrorResponseResult(result)) {
        if (result.status === 402) setPaymentFailed(true);
        else if (result.status === 400 && typeof (result.error as any)?.message === 'string')
          // `error` is the parsed body, `{message}` from BaseHandler.write_error;
          // `result.message` is only the generic "Request … failed with 400".
          setServerMessage((result.error as any).message);
        else setError(true);
        return;
      }
      await queryClient.invalidateQueries([queryNames.subscription]);
      showSuccess(
        intl.formatMessage(
          isUpgrade
            ? messages.changePlanUpgradedToast
            : messages.changePlanScheduledToast,
          { plan: planName },
        ),
      );
      closeModal();
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal>
      <div className={styles.cancelModal}>
        {loading ? (
          <Loader />
        ) : !preview ? (
          <p className={styles.checkoutError} role="alert">
            <FormattedMessage {...messages.changePlanError} />
          </p>
        ) : (
          <>
            <h3 className={styles.cancelModalTitle}>
              <FormattedMessage
                {...(isUpgrade
                  ? messages.changePlanModalTitleUpgrade
                  : messages.changePlanModalTitleDowngrade)}
                values={{ plan: planName }}
              />
            </h3>

            <p className={styles.cancelModalIntro}>
              {isUpgrade ? (
                <FormattedMessage
                  {...(amountNow ? messages.changePlanUpgradeBodyAmount : messages.changePlanUpgradeBody)}
                  values={{ plan: planName, amount: <strong>{amountNow}</strong> }}
                />
              ) : (
                <FormattedMessage
                  {...(preview.effective_at
                    ? messages.changePlanDowngradeBody
                    : messages.changePlanDowngradeBodyNoDate)}
                  values={{
                    plan: planName,
                    effectiveAt: preview.effective_at
                      ? new Date(preview.effective_at)
                      : undefined,
                  }}
                />
              )}
            </p>

            {serverMessage && (
              <p className={styles.checkoutError} role="alert" data-test="change-plan-server-message">
                {serverMessage}
              </p>
            )}
            {(error || paymentFailed) && (
              <p className={styles.checkoutError} role="alert">
                <FormattedMessage
                  {...(paymentFailed ? messages.changePlanPaymentFailed : messages.changePlanError)}
                />
              </p>
            )}

            <div className={styles.cancelModalActions}>
              <Button mode="secondary" size="small" onClick={closeModal} disabled={submitting}>
                <FormattedMessage {...messages.changePlanCancel} />
              </Button>
              <Button
                mode="primary"
                size="small"
                onClick={onConfirm}
                disabled={submitting}
                loading={submitting}
              >
                <FormattedMessage
                  {...(submitting
                    ? messages.changePlanConfirming
                    : isUpgrade
                    ? messages.changePlanConfirmUpgrade
                    : messages.changePlanConfirmDowngrade)}
                />
              </Button>
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default ChangePlanModal;
